import React, { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const CHECK_INTERVAL_MS = 60000;    // re-check every 60s (was 20s)
const HEALTH_TIMEOUT_MS = 8000;     // consider unhealthy only if health takes >8s (was 4s)
const CONSECUTIVE_FAILS_TO_SHOW = 3; // require 3 in a row before showing (avoids flapping on transient issues)

/**
 * Subtle top strip shown ONLY when the backend is genuinely hard to reach.
 * Hides immediately when health recovers. Requires 3 consecutive failures before showing.
 * User can also dismiss it manually per-session.
 */
export default function MaintenanceBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem("maintenance-banner-dismissed") === "1";
    } catch (_) {
      return false;
    }
  });

  useEffect(() => {
    let cancelled = false;
    let failCount = 0;

    const check = async () => {
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
      try {
        const res = await fetch(`${API}/api/health`, {
          signal: controller.signal,
          cache: "no-store",
        });
        if (cancelled) return;
        if (res.ok) {
          // Healthy — reset counter and immediately hide
          failCount = 0;
          setShowBanner(false);
        } else {
          failCount += 1;
          if (failCount >= CONSECUTIVE_FAILS_TO_SHOW) setShowBanner(true);
        }
      } catch (_) {
        if (cancelled) return;
        failCount += 1;
        if (failCount >= CONSECUTIVE_FAILS_TO_SHOW) setShowBanner(true);
      } finally {
        clearTimeout(t);
      }
    };

    check();
    const id = setInterval(check, CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (!showBanner || dismissed) return null;

  return (
    <div
      data-testid="maintenance-banner"
      className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-2 px-3 py-1.5 text-[13px] font-medium text-amber-950 bg-amber-100/95 backdrop-blur-sm border-b border-amber-300 shadow-sm"
      role="status"
      aria-live="polite"
    >
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-700" />
      <span className="truncate">
        We&rsquo;re upgrading our infrastructure &mdash; some features may be limited.
      </span>
      <button
        type="button"
        onClick={() => {
          setDismissed(true);
          try {
            sessionStorage.setItem("maintenance-banner-dismissed", "1");
          } catch (_) { /* ignore */ }
        }}
        aria-label="Dismiss maintenance notice"
        data-testid="maintenance-banner-dismiss"
        className="ml-1 rounded p-0.5 text-amber-800 hover:bg-amber-200/70 transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
